import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { BookOpen, Menu, X } from "lucide-react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "How It Works", href: "#how-it-works" },
    { label: "Stories", href: "#story-preview" },
    { label: "Reviews", href: "#social-proof" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl shadow-lg shadow-rose-900/5 border-b border-indigo-950/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-10 h-10 rounded-2xl bg-rose-500 flex items-center justify-center shadow-lg shadow-rose-500/30 group-hover:shadow-rose-500/50 transition-shadow">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-indigo-950 tracking-tight" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Bala<span className="text-rose-500">katha</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-semibold text-indigo-950/60 hover:text-rose-500 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <Button
              data-testid="header-cta-button"
              className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-bold px-6 shadow-lg shadow-rose-500/25 hover:-translate-y-0.5 hover:shadow-xl transition-all"
              onClick={() => document.getElementById("hero")?.scrollIntoView({ behavior: "smooth" })}
            >
              Create a Book
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-xl hover:bg-rose-50 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6 text-indigo-950" /> : <Menu className="w-6 h-6 text-indigo-950" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-indigo-950/5 pb-4" data-testid="mobile-menu">
          <div className="px-4 pt-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-2 text-base font-semibold text-indigo-950/70 hover:text-rose-500 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <Button
              className="w-full rounded-full bg-rose-500 hover:bg-rose-600 text-white font-bold shadow-lg"
              onClick={() => {
                setMobileOpen(false);
                document.getElementById("hero")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Create a Book
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
